import { useState, useCallback } from 'react'
import { useAuthStore } from '../stores/authStore'
import { PalaceDecoration } from '../components/icons/GameIcons'
import './HallPage.css'

interface HallPageProps {
  onNewGame: () => void
  onLoadGame: () => void
  onProfile: () => void
  onHome?: () => void
}

type MenuItem = 'newGame' | 'loadGame' | 'profile' | null

export function HallPage({ onNewGame, onLoadGame, onProfile, onHome }: HallPageProps) {
  const { user, logout, openLoginPanel } = useAuthStore()
  const [dissolvingItem, setDissolvingItem] = useState<MenuItem>(null)

  const handleLogout = async () => {
    await logout()
  }

  // 脱敏显示邮箱
  const maskEmail = (email: string) => {
    const [name, domain] = email.split('@')
    if (name.length <= 2) return `*@${domain}`
    return `${name[0]}${'*'.repeat(name.length - 2)}${name[name.length - 1]}@${domain}`
  }

  // 处理菜单点击 - 水墨消散效果
  const handleMenuClick = useCallback((item: MenuItem, callback: () => void) => {
    if (dissolvingItem) return // 防止重复点击
    
    setDissolvingItem(item)
    
    // 等待消散动画完成后跳转
    setTimeout(() => {
      callback()
    }, 800) // 800ms 消散动画时间
  }, [dissolvingItem])

  return (
    <div className="hall-page">
      {/* 背景装饰 */}
      <div className="hall-background">
        <div className="cartoon-bg"></div>
      </div>

      {/* 顶部标题区 */}
      <header className="hall-header">
        <div className="header-decoration left">
          <div className="cloud-pattern"></div>
        </div>
        
        <div className="title-wrapper">
          <h1 className="hall-title">主公阁</h1>
          <p className="hall-subtitle">天下大势，尽在掌握</p>
        </div>
        
        <div className="header-decoration right">
          <div className="cloud-pattern"></div>
        </div>
      </header>

      {/* 宫殿装饰 */}
      <div className="palace-decoration">
        <PalaceDecoration size={300} />
      </div>

      {/* 主内容区 - 水墨菜单 */}
      <main className="hall-content">
        <nav className="ink-menu">
          {/* 新建霸业 */}
          <button 
            className={`ink-item ${dissolvingItem === 'newGame' ? 'dissolving' : ''} ${dissolvingItem && dissolvingItem !== 'newGame' ? 'faded' : ''}`}
            onClick={() => handleMenuClick('newGame', onNewGame)}
            disabled={!!dissolvingItem}
          >
            <span className="ink-text">新建霸业</span>
            <span className="ink-subtext">开启新的征程</span>
            <div className="ink-blob"></div>
            <div className="ink-drops">
              <span></span><span></span><span></span><span></span><span></span>
            </div>
          </button>

          {/* 载入存档 */}
          <button 
            className={`ink-item ${dissolvingItem === 'loadGame' ? 'dissolving' : ''} ${dissolvingItem && dissolvingItem !== 'loadGame' ? 'faded' : ''}`}
            onClick={() => handleMenuClick('loadGame', onLoadGame)}
            disabled={!!dissolvingItem}
          >
            <span className="ink-text">载入存档</span>
            <span className="ink-subtext">继续未竟的事业</span>
            <div className="ink-blob"></div>
            <div className="ink-drops">
              <span></span><span></span><span></span><span></span><span></span>
            </div>
          </button>

          {/* 君主档案 */}
          <button 
            className={`ink-item ${dissolvingItem === 'profile' ? 'dissolving' : ''} ${dissolvingItem && dissolvingItem !== 'profile' ? 'faded' : ''}`}
            onClick={() => handleMenuClick('profile', onProfile)}
            disabled={!!dissolvingItem}
          >
            <span className="ink-text">君主档案</span>
            <span className="ink-subtext">查看个人信息</span>
            <div className="ink-blob"></div>
            <div className="ink-drops">
              <span></span><span></span><span></span><span></span><span></span>
            </div>
          </button>
        </nav>
      </main>

      {/* 底部信息栏 */}
      <footer className="hall-footer">
        <div className="footer-content">
          {/* 返回首页 */}
          {onHome && (
            <>
              <button className="footer-btn home-btn" onClick={onHome}>
                <svg className="btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                <span className="btn-text">返回首页</span>
              </button>
              
              <div className="footer-divider">
                <span className="divider-dot"></span>
              </div>
            </>
          )}
          
          {/* 用户信息 */}
          <div className="user-info">
            <span className="user-label">当前君主</span>
            {!user ? (
              <button 
                className="user-email not-logged login-trigger" 
                onClick={openLoginPanel}
                title="点击登录"
              >
                未登录
                <svg className="login-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
              </button>
            ) : (
              <span className="user-email">
                {maskEmail(user.email)}
              </span>
            )}
          </div>
          
          {/* 退出登录 - 仅登录时显示 */}
          {user && (
            <>
              <div className="footer-divider">
                <span className="divider-dot"></span>
              </div>
              
              <button className="footer-btn logout-btn" onClick={handleLogout}>
                <svg className="btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                <span className="btn-text">退出登录</span>
              </button>
            </>
          )}
        </div>
      </footer>
    </div>
  )
}
